const cleanValue = (value) => String(value ?? '').trim();

const normalizeValue = (value) => cleanValue(value).toUpperCase();

const paperRollKey = (row) => `${normalizeValue(row?.work_order)}\u0000${normalizeValue(row?.roll_no)}`;

export const fillExistingPaperRollSeries = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const seriesByRoll = new Map();

  list.forEach((row) => {
    const rollKey = normalizeValue(row?.roll_no);
    const scopedRollKey = paperRollKey(row);
    const serial = cleanValue(row?.serial_or_part);
    const serialKey = normalizeValue(serial);
    if (!rollKey || !serialKey) return;

    const existing = seriesByRoll.get(scopedRollKey);
    if (!existing) {
      seriesByRoll.set(scopedRollKey, { key: serialKey, value: serial, conflict: false });
    } else if (existing.key !== serialKey) {
      existing.conflict = true;
    }
  });

  return list.map((row) => {
    const existing = seriesByRoll.get(paperRollKey(row));
    if (!existing || existing.conflict) return { ...row };

    return { ...row, serial_or_part: existing.value };
  });
};

export const synchronizePaperRollRows = (rows, rowId, field, value) => {
  const list = Array.isArray(rows) ? rows : [];
  const changedIndex = list.findIndex((row) => row.id === rowId);
  if (changedIndex < 0) return list;

  const next = list.map((row) => ({ ...row }));
  const changedRow = next[changedIndex];

  if (field === 'roll_no') {
    const previousRollKey = normalizeValue(changedRow.roll_no);
    changedRow.roll_no = value;
    const rollKey = normalizeValue(value);
    const workOrderKey = normalizeValue(changedRow.work_order);

    if (rollKey !== previousRollKey) {
      changedRow.serial_or_part = '';
    }

    if (rollKey) {
      const existingRow = next.find((row, index) =>
        index !== changedIndex &&
        normalizeValue(row.work_order) === workOrderKey &&
        normalizeValue(row.roll_no) === rollKey &&
        normalizeValue(row.serial_or_part)
      );

      if (existingRow) {
        changedRow.serial_or_part = cleanValue(existingRow.serial_or_part);
      }
    }

    return next;
  }

  if (field === 'serial_or_part') {
    const rollKey = normalizeValue(changedRow.roll_no);
    const workOrderKey = normalizeValue(changedRow.work_order);

    next.forEach((row, index) => {
      if (index === changedIndex || (
        rollKey &&
        normalizeValue(row.work_order) === workOrderKey &&
        normalizeValue(row.roll_no) === rollKey
      )) {
        row.serial_or_part = value;
      }
    });

    return next;
  }

  if (field === 'work_order') {
    changedRow.work_order = value;
    const rollKey = normalizeValue(changedRow.roll_no);
    const workOrderKey = normalizeValue(value);

    if (rollKey && !normalizeValue(changedRow.serial_or_part)) {
      const existingRow = next.find((row, index) =>
        index !== changedIndex &&
        normalizeValue(row.work_order) === workOrderKey &&
        normalizeValue(row.roll_no) === rollKey &&
        normalizeValue(row.serial_or_part)
      );

      if (existingRow) {
        changedRow.serial_or_part = cleanValue(existingRow.serial_or_part);
      }
    }

    return next;
  }

  changedRow[field] = value;
  return next;
};

export const validatePaperRollSeries = (rows, { requireAllRows = false } = {}) => {
  const list = Array.isArray(rows) ? rows : [];
  const rollToSerial = new Map();
  const serialToRoll = new Map();
  const issues = [];
  const issueKeys = new Set();

  list.forEach((row, index) => {
    const roll = cleanValue(row?.roll_no);
    const workOrder = cleanValue(row?.work_order);
    const serial = cleanValue(row?.serial_or_part);
    const rollKey = normalizeValue(roll);
    const workOrderKey = normalizeValue(workOrder);
    const serialKey = normalizeValue(serial);
    const scopedRollKey = `${workOrderKey}\u0000${rollKey}`;
    const scopedSerialKey = `${workOrderKey}\u0000${serialKey}`;

    if (!rollKey || !serialKey) return;

    const existingSerial = rollToSerial.get(scopedRollKey);
    if (existingSerial && existingSerial.key !== serialKey) {
      const key = `roll:${scopedRollKey}`;
      if (!issueKeys.has(key)) {
        issues.push({
          type: 'roll_has_multiple_series',
          workOrder,
          roll: existingSerial.roll,
          values: [existingSerial.serial, serial],
          rowNumbers: [existingSerial.rowNumber, index + 1],
        });
        issueKeys.add(key);
      }
    } else if (!existingSerial) {
      rollToSerial.set(scopedRollKey, {
        key: serialKey,
        roll,
        serial,
        rowNumber: index + 1,
      });
    }

    const existingRoll = serialToRoll.get(scopedSerialKey);
    if (existingRoll && existingRoll.key !== rollKey) {
      const key = `serial:${scopedSerialKey}`;
      if (!issueKeys.has(key)) {
        issues.push({
          type: 'series_has_multiple_rolls',
          workOrder,
          serial: existingRoll.serial,
          values: [existingRoll.roll, roll],
          rowNumbers: [existingRoll.rowNumber, index + 1],
        });
        issueKeys.add(key);
      }
    } else if (!existingRoll) {
      serialToRoll.set(scopedSerialKey, {
        key: rollKey,
        roll,
        serial,
        rowNumber: index + 1,
      });
    }
  });

  list.forEach((row, index) => {
    const roll = cleanValue(row?.roll_no);
    const workOrder = cleanValue(row?.work_order);
    const serial = cleanValue(row?.serial_or_part);
    const rollKey = normalizeValue(roll);
    const workOrderKey = normalizeValue(workOrder);
    const scopedRollKey = `${workOrderKey}\u0000${rollKey}`;

    const hasProductionData = [row?.quantity, row?.counter, row?.bags_good, row?.scrap]
      .some((value) => value !== '' && value != null);
    const hasAnyRowData = [row?.item, row?.work_order, row?.pallet_no, row?.serial_or_part]
      .some((value) => cleanValue(value) !== '') || hasProductionData;
    if (!rollKey && (hasProductionData || serial || (requireAllRows && hasAnyRowData))) {
      issues.push({
        type: 'missing_roll',
        workOrder,
        rowNumbers: [index + 1],
      });
      return;
    }

    if (!rollKey || serial || rollToSerial.has(scopedRollKey)) return;

    issues.push({
      type: 'roll_missing_series',
      workOrder,
      roll,
      rowNumbers: [index + 1],
    });
  });

  const warningRowIds = new Set();
  const missingSeriesRowIds = new Set();
  const missingRollRowIds = new Set();
  issues.forEach((issue) => {
    if (issue.type === 'missing_roll') {
      const row = list[issue.rowNumbers[0] - 1];
      if (row?.id != null) missingRollRowIds.add(row.id);
      return;
    }
    if (issue.type === 'roll_missing_series') {
      const row = list[issue.rowNumbers[0] - 1];
      if (row?.id != null) {
        warningRowIds.add(row.id);
        missingSeriesRowIds.add(row.id);
      }
      return;
    }

    const lastConflictingRow = [...list].reverse().find((row) => {
      if (normalizeValue(row?.work_order) !== normalizeValue(issue.workOrder)) return false;

      if (issue.type === 'roll_has_multiple_series') {
        return normalizeValue(row?.roll_no) === normalizeValue(issue.roll) &&
          Boolean(normalizeValue(row?.serial_or_part));
      }

      return normalizeValue(row?.serial_or_part) === normalizeValue(issue.serial) &&
        Boolean(normalizeValue(row?.roll_no));
    });

    if (lastConflictingRow) warningRowIds.add(lastConflictingRow.id);
  });

  return {
    isValid: issues.length === 0,
    issues,
    warningRowIds,
    missingSeriesRowIds,
    missingRollRowIds,
  };
};

export const formatPaperRollIssue = (issue) => {
  const context = issue.workOrder ? ` en el Work Order ${issue.workOrder}` : '';

  if (issue.type === 'missing_roll') {
    return `Falta el número de rollo${context} (renglón ${issue.rowNumbers[0]}).`;
  }

  if (issue.type === 'roll_missing_series') {
    return `El rollo ${issue.roll}${context} no tiene Serie de Papel (renglón ${issue.rowNumbers[0]}).`;
  }

  if (issue.type === 'roll_has_multiple_series') {
    return `El rollo ${issue.roll}${context} tiene más de una Serie de Papel (${issue.values.join(' y ')}).`;
  }

  return `La Serie de Papel ${issue.serial}${context} está asignada a más de un rollo (${issue.values.join(' y ')}).`;
};
